"use client"

import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import styled from "@emotion/styled"
import CloseIcon from "@mui/icons-material/Close"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { Box, css, IconButton, Typography } from "@mui/material"
import { useLocale, useTranslations } from "next-intl"
import { useEffect, useState } from "react"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import ErrorBoundary from "./ErrorBoundary"
import InvestmentFilters from "./InvestmentFilters"
import InvestmentForm from "./InvestmentForm"
import InvestmentGraph from "./InvestmentGraph"
import TimeFrameSelector from "./TimeFrameSelector"
import {
  filterDataByTimeFrame,
  getInvestmentChartOptions,
  processChartData,
} from "./chartOptions"
import { InvestmentSimulatorProps, TimeFrameType } from "./types"

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fullWidth",
})<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${sizes.baseTriple};
  background-color: ${colors.white};
  border-radius: ${sizes.borderRadius};
  ${mediaQueries.desktopM} {
    padding: ${sizes.baseQuadric};
    width: 570px;
    ${({ fullWidth }) =>
      fullWidth &&
      css`
        width: 100%;
      `}
  }
  position: relative;
`

const StyledInfoIconWrapper = styled(Box)`
  position: absolute;
  top: ${sizes.baseSingle};
  inset-inline-end: ${sizes.baseSingle};
  z-index: 2;
  ${mediaQueries.desktopM} {
    top: ${sizes.baseDouble};
    inset-inline-end: ${sizes.baseDouble};
  }
`

const StyledCustomTooltip = styled("div")`
  background: ${colors.white};
  color: ${colors.black};
  border-radius: 16px;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.12);
  padding: 18px 20px 18px 20px;
  width: 320px;
  font-size: 15px;
  line-height: 1.6;
  font-family: inherit;
  position: absolute;
  top: 0;
  inset-inline-start: auto;
  inset-inline-end: 0;
  z-index: 1;
  word-break: break-word;
`

const StyledTooltipCloseButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  inset-inline-end: 8px;
  color: ${colors.gray};
  z-index: 2;
`

const StyledFooterText = styled(Typography)`
  display: none;
  ${mediaQueries.desktopM} {
    display: block;
  }
`

const StyledHeading = styled(Typography)`
  margin-bottom: ${sizes.baseTriple};
  color: ${colors.blue};
  text-transform: uppercase;
`

const StyledErrorMessage = styled(Typography)`
  color: ${colors.white};
  text-align: start;
  width: 100%;
  direction: rtl;
  font-family: monospace;
  font-size: 10px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background-color: ${colors.red};
  margin-top: 10px;
`

const InvestmentSimulator = ({
  apiData,
  isMockData = false,
  fullWidth,
  tooltipData,
}: InvestmentSimulatorProps) => {
  const t = useTranslations("InvestSimulator")

  // Get current month and year for tooltip, localized by current locale

  const locale = useLocale?.() || "he"
  const now = new Date()
  const resolvedLocale = locale.startsWith("en") ? "en-US" : "he-IL"
  const month = now.toLocaleString(resolvedLocale, { month: "long" })
  const year = now.getFullYear()
  const monthYear = `${month} ${year}`
  const [investment, setInvestment] = useState<number>()
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(
    apiData?.companies?.[0]?.id || 0,
  )
  const [selectedFundId, setSelectedFundId] = useState<number>(
    apiData?.funds?.[0]?.id || 0,
  )
  const [timeFrame, setTimeFrame] = useState<TimeFrameType>("12m")
  const [chartOptions, setChartOptions] = useState(getInvestmentChartOptions())
  const [chartSeries, setChartSeries] = useState<any[]>([])
  const [estimatedValue, setEstimatedValue] = useState<number | null>(null)
  const [profitPercentage, setProfitPercentage] = useState<number | null>(null)

  // Initialize the chart with raw data
  useEffect(() => {
    updateChartWithRawData()
    // Also update investment estimate when time frame changes if already submitted
    if (submitted) {
      calculateEstimatedValue()
    }
  }, [selectedCompanyId, selectedFundId, timeFrame])

  // Update the estimation when investment is submitted or amount changes
  useEffect(() => {
    if (submitted) {
      calculateEstimatedValue()
    }
  }, [investment, submitted])

  const updateChartWithRawData = () => {
    const companyInvestment = apiData?.companiesInvestmentData?.find(
      (data) => data?.companyId === selectedCompanyId,
    )
    if (!companyInvestment) return

    const fundData = companyInvestment?.fundsData?.find(
      (fd) => fd?.fundId === selectedFundId,
    )
    if (!fundData || !fundData?.dataList || fundData?.dataList?.length === 0) {
      // Set empty chart if no data
      setChartSeries([])
      return
    }

    // Process the data points
    const allData = fundData?.dataList?.map((item, index) => {
      // Create a date object from the date string
      const dateObj = new Date(item?.date || "")

      // Generate a placeholder value if none exists
      const value = item?.value !== undefined ? item.value : 100 + index * 0.5
      return { dateObj, value }
    })

    // Filter data based on time frame
    const displayData = filterDataByTimeFrame(allData, timeFrame)

    // Create chart series with raw values
    let seriesData = displayData?.map((item) => ({
      x: item?.dateObj,
      y: item?.value,
    }))

    // Apply data smoothing and reduction
    seriesData = processChartData(seriesData, timeFrame)

    setChartSeries([{ name: t("chartSeriesName"), data: seriesData }])
    setChartOptions(getInvestmentChartOptions())
  }

  const calculateEstimatedValue = () => {
    if (!investment || investment <= 0) return

    const companyInvestment = apiData?.companiesInvestmentData?.find(
      (data) => data?.companyId === selectedCompanyId,
    )
    if (!companyInvestment) return

    const fundData = companyInvestment?.fundsData?.find(
      (fd) => fd?.fundId === selectedFundId,
    )
    if (!fundData || !fundData?.dataList || fundData?.dataList?.length === 0)
      return

    // Process the data points
    const allData = fundData?.dataList?.map((item, index) => {
      const dateObj = new Date(item?.date || "")
      const value = item?.value !== undefined ? item.value : 100 + index * 0.5
      return { dateObj, value }
    })

    // Filter data based on time frame
    const displayData = filterDataByTimeFrame(allData, timeFrame)

    // Get first and last values for estimation
    const periodStartValue = displayData?.[0]?.value
    const latestValue = displayData?.[displayData?.length - 1]?.value

    // Calculate the estimated value
    if (periodStartValue && latestValue) {
      const estimate = investment * (latestValue / periodStartValue)
      setEstimatedValue(estimate)

      // Calculate profit percentage
      const profit = estimate - investment
      const profitPercent = (profit / investment) * 100
      setProfitPercentage(profitPercent)
    }
  }

  const handleSubmit = () => {
    if (investment !== undefined && investment > 0) setSubmitted(true)
  }

  const handleCompanyChange = (companyId: number) => {
    setSelectedCompanyId(companyId)

    // Reset fund selection to first available fund for this company
    const company = apiData?.companiesInvestmentData?.find(
      (data) => data?.companyId === companyId,
    )
    if (company && company?.fundsData?.length > 0) {
      setSelectedFundId(company?.fundsData?.[0]?.fundId)
    }
  }

  const handleFundChange = (fundId: number) => {
    setSelectedFundId(fundId)
  }

  const handleTimeFrameChange = (newTimeFrame: TimeFrameType) => {
    setTimeFrame(newTimeFrame)
  }

  const formatNumber = (num: number) => {
    return num.toLocaleString("he-IL")
  }

  const resetSimulation = () => {
    setSubmitted(false)
    setInvestment(undefined)
    setSelectedCompanyId(apiData?.companies?.[0]?.id || 1)
    setSelectedFundId(apiData?.funds?.[0]?.id || 1)
    setTimeFrame("12m")
  }

  const [tooltipOpen, setTooltipOpen] = useState(false)
  const handleTooltipToggle = () => setTooltipOpen((prev) => !prev)

  // Close tooltip on click outside
  useEffect(() => {
    if (!tooltipOpen) return
    const handleClick = (e: MouseEvent) => {
      const tooltip = document.getElementById("investment-tooltip-popover")
      const icon = document.getElementById("investment-tooltip-icon")
      if (
        tooltip &&
        !tooltip.contains(e.target as Node) &&
        icon &&
        !icon.contains(e.target as Node)
      ) {
        setTooltipOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [tooltipOpen])

  return (
    <StyledContainer fullWidth={fullWidth}>
      <StyledInfoIconWrapper>
        <IconButton
          id="investment-tooltip-icon"
          size="small"
          sx={{ color: colors.gray, zIndex: 2 }}
          onClick={handleTooltipToggle}
          aria-label={tooltipOpen ? "סגור מידע" : "מידע נוסף"}
        >
          {tooltipOpen ? (
            <CloseIcon fontSize="small" />
          ) : (
            <InfoOutlinedIcon fontSize="small" />
          )}
        </IconButton>
        {tooltipOpen && (
          <StyledCustomTooltip
            id="investment-tooltip-popover"
            role="tooltip"
            dangerouslySetInnerHTML={{
              __html: replaceRichTextLinks(tooltipData || ""),
            }}
          />
        )}
      </StyledInfoIconWrapper>
      {!submitted && <StyledHeading variant="h4">{t("heading")}</StyledHeading>}

      <InvestmentForm
        investment={investment}
        setInvestment={setInvestment}
        submitted={submitted}
        estimatedValue={estimatedValue}
        profitPercentage={profitPercentage}
        formatNumber={formatNumber}
        handleSubmit={handleSubmit}
      />

      <InvestmentFilters
        apiData={apiData}
        selectedCompanyId={selectedCompanyId}
        selectedFundId={selectedFundId}
        onCompanyChange={handleCompanyChange}
        onFundChange={handleFundChange}
      />

      <ErrorBoundary>
        <InvestmentGraph
          chartOptions={chartOptions}
          chartSeries={chartSeries}
          fullWidth={fullWidth}
        />
      </ErrorBoundary>

      <TimeFrameSelector
        timeFrame={timeFrame}
        onChange={handleTimeFrameChange}
        resetSimulation={resetSimulation}
      />

      <StyledFooterText variant="caption" color={colors.gray} sx={{ mt: 2 }}>
        {t("footerNote")}
      </StyledFooterText>

      {/* show error message if we are using mock data */}
      {isMockData && (
        <StyledErrorMessage variant="caption">
          {t("errorMessage")}
        </StyledErrorMessage>
      )}
    </StyledContainer>
  )
}

export default InvestmentSimulator
